/* eslint-disable import/no-extraneous-dependencies */
import { tabsAnatomy as parts } from '@chakra-ui/anatomy';
import {
    createMultiStyleConfigHelpers,
    cssVar,
} from '@chakra-ui/styled-system';

// eslint-disable-next-line @typescript-eslint/unbound-method
const { definePartsStyle } = createMultiStyleConfigHelpers(parts.keys);

const $fg = cssVar('tabs-color');
const $bg = cssVar('tabs-bg');
const $border = cssVar('tabs-border-color');
const $borderSelected = cssVar('tabs-selected-border-color');
const $borderUnselected = cssVar('tabs-unselected-border-color');
const $borderTabPanel = cssVar('tabpanel-border-color');
const $borderBottomColor = cssVar('tabs-bottom-border-color');

// Adapted from Chakra's Line Variant
// https://github.com/chakra-ui/chakra-ui/blob/v2/packages/theme/src/components/tabs.ts
const variantUnderline = definePartsStyle((props) => {
    const { colorScheme: c, orientation } = props;
    const isVertical = orientation === 'vertical';
    const borderProp = isVertical ? 'borderStart' : 'borderBottom';
    const marginProp = isVertical ? 'marginStart' : 'marginBottom';

    return {
        tablist: {
            [borderProp]: '2px solid',
            borderColor: 'transparent',
        },
        tab: {
            [borderProp]: '2px solid',
            borderColor: 'transparent',
            [marginProp]: '-2px',
            _selected: {
                color: 'black',
                [$fg.variable]: `colors.${c}.500`,
                _dark: {
                    [$fg.variable]: `colors.${c}.300`,
                },
                _hover: {
                    borderColor: `${c}.500`,
                },
                borderColor: `${c}.500`,
                fontWeight: '500',
            },
            _focus: {
                _hover: {
                    borderColor: `${c}.500`,
                },
            },
            _disabled: {
                _active: { bg: 'none' },
            },
            _hover: {
                borderColor: 'muted.300',
                [$fg.variable]: 'colors.muted.500',
            },
            [$fg.variable]: 'colors.muted.300',
            fontWeight: 'regular',
            color: 'black',
            bg: $bg.reference,
            py: 3,
            px: 0,
        },
    };
});

const variantEnclosedBasic = definePartsStyle((props) => {
    const { colorScheme: c } = props;
    return {
        tab: {
            border: '1px solid',
            borderColor: $borderUnselected.reference,
            borderBottomColor: $borderBottomColor.reference,
            mb: '-1px',
            p: 0,
            [$borderSelected.variable]: `colors.${c}.200`,
            [$border.variable]: 'transparent',
            [$borderUnselected.variable]: 'colors.muted.50',
            [$borderBottomColor.variable]: `colors.${c}.200`,
            color: $fg.reference,
            [$fg.variable]: 'colors.muted.500',
            _selected: {
                [$border.variable]: `colors.${c}.200`,
                _dark: {
                    [$fg.variable]: `colors.${c}.300`,
                    [$border.variable]: 'colors.gray.800',
                },
                borderColor: $border.reference,
                borderBottomColor: 'white',
                [$fg.variable]: 'black',
            },
            _first: {
                borderTopLeftRadius: 'md',
            },
            _last: {
                borderTopRightRadius: 'md',
            },
            _disabled: {
                _selected: { opacity: 1 },
            },
        },
        tablist: {
            mb: '-1px',
            borderBottom: '1px solid',
            borderColor: 'white',
        },
        tabpanel: {
            border: '1px solid',
            [$borderTabPanel.variable]: `colors.${c}.200`,
            borderColor: $borderTabPanel.reference,
            borderBottomRadius: 'md',
        },
    };
});

export const tabsTheme = {
    baseStyle: {
        tab: {
            fontWeight: 'bold',
        },
        tablist: {
            width: 'fit-content',
        },
    },
    defaultProps: {
        size: 'md',
    },
    variants: {
        underline: variantUnderline,
        'enclosed-basic': variantEnclosedBasic,
    },
};
